import React, { FC, useCallback, useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { getFilters } from '../redux/slices/filters';
import { Filter } from '../types/OilsFIlter.types';
import { FilterOptions } from '../types/filter.types';

import OilSelect from '../components/OilSelect/OilSelect';

export const useOilSelects = (
  labels: {
    [filter in Filter]: { label: string; placeholder: string };
  },
  handleFilterChange: (filterValue: string, filterName: Filter) => Promise<void>
) => {
  const filters = useAppSelector(getFilters);

  // ----- value getter fns -----

  const getOptionsList = useCallback(
    (options: FilterOptions['options']) => options.map((o) => o.name),
    []
  );

  const getValue = useCallback(
    (filterName: Filter) =>
      filters[filterName].options.find(
        (o) => o.id === filters[filterName].value
      )?.name ?? '',
    [filters]
  );

  // ----- components -----

  const CountryOilSelect: FC = useMemo(() => {
    return () => (
      <OilSelect
        label={labels.country.label}
        placeholder={labels.country.placeholder}
        options={getOptionsList(filters['country'].options)}
        value={getValue('country')}
        onChange={(newValue) => handleFilterChange(newValue, 'country')}
      />
    );
  }, [labels, getOptionsList, getValue, handleFilterChange]);

  const ApplicationOilSelect: FC = useMemo(() => {
    return () => (
      <OilSelect
        label={labels.application.label}
        placeholder={labels.application.placeholder}
        options={getOptionsList(filters['application'].options)}
        value={getValue('application')}
        onChange={(newValue) => handleFilterChange(newValue, 'application')}
      />
    );
  }, [labels, getOptionsList, getValue, handleFilterChange]);

  const SubApplicationOilSelect: FC = useMemo(() => {
    return () => (
      <OilSelect
        label={labels.subapplication.label}
        placeholder={labels.subapplication.placeholder}
        options={getOptionsList(filters['subapplication'].options)}
        value={getValue('subapplication')}
        onChange={(newValue) => handleFilterChange(newValue, 'subapplication')}
      />
    );
  }, [labels, getOptionsList, getValue, handleFilterChange]);

  const SeriesOilSelect: FC = useMemo(() => {
    return () => (
      <OilSelect
        label={labels.series.label}
        placeholder={labels.series.placeholder}
        options={getOptionsList(filters['series'].options)}
        value={getValue('series')}
        onChange={(newValue) => handleFilterChange(newValue, 'series')}
      />
    );
  }, [labels, getOptionsList, getValue, handleFilterChange]);

  const HnEOilSelect: FC = useMemo(() => {
    return () => (
      <OilSelect
        label={labels.homoAndMiss.label}
        placeholder={labels.homoAndMiss.placeholder}
        options={getOptionsList(filters['homoAndMiss'].options)}
        value={getValue('homoAndMiss')}
        onChange={(newValue) => handleFilterChange(newValue, 'homoAndMiss')}
      />
    );
  }, [labels, getOptionsList, getValue, handleFilterChange]);

  return {
    CountryOilSelect,
    ApplicationOilSelect,
    SubApplicationOilSelect,
    SeriesOilSelect,
    HnEOilSelect,
  };
};
